import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import AdminUserManagement from './components/AdminUserManagement';
import ComingSoonSettings from './components/ComingSoonSettings';
import SystemHealthPanel from './components/SystemHealthPanel';
import FeaturedContentSettings from './components/FeaturedContentSettings';
import { comingSoonService } from '../../services/comingSoonService';
import { adminService } from '../../services/adminService';

const SystemSettings = () => {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [comingSoonSettings, setComingSoonSettings] = useState(null);

  // Check admin status and load initial data
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || authLoading) return;

      try {
        setLoading(true);
        const adminStatus = await adminService?.isCurrentUserAdmin();
        setIsAdmin(adminStatus);

        if (adminStatus) {
          // Load coming soon settings
          const { data: settings } = await comingSoonService?.getSettings();
          setComingSoonSettings(settings);
        }
      } catch (err) {
        setError('Failed to load admin panel');
        console.error('Admin panel error:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, authLoading]);

  // Handle coming soon settings update
  const handleComingSoonUpdate = async (newSettings) => {
    try {
      const { data, error } = await comingSoonService?.updateSettings(newSettings, user?.id);
      if (error) throw error;
      
      setComingSoonSettings(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message };
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading admin panel...</span>
        </div>
      </div>
    );
  }

  // Access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Shield" className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin panel.
          </p>
          <a 
            href="/homepage"
            className="inline-flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Home</span>
          </a>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Error</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button 
            onClick={() => window.location?.reload()}
            className="inline-flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="RotateCcw" size={16} />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      description: 'Manage user accounts, roles, and permissions'
    },
    {
      id: 'coming-soon',
      label: 'Coming Soon Settings',
      icon: 'Settings',
      description: 'Configure coming soon page and access controls'
    },
    {
      id: 'featured-content',
      label: 'Featured Content',
      icon: 'Image',
      description: 'Manage hero section backgrounds and featured text'
    },
    {
      id: 'system',
      label: 'System Health',
      icon: 'Activity',
      description: 'Monitor system performance and health metrics'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">System Settings</h1>
                  <p className="text-sm text-muted-foreground">
                    Platform Configuration Hub
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">Administrator</p>
              </div>
              <a 
                href="/homepage"
                className="flex items-center space-x-2 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Site</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'bg-card hover:bg-muted border border-border'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon 
                      name={tab?.icon} 
                      size={20} 
                      className={activeTab === tab?.id ? 'text-primary-foreground' : 'text-muted-foreground'} 
                    />
                    <div>
                      <h3 className={`font-medium ${
                        activeTab === tab?.id ? 'text-primary-foreground' : 'text-foreground'
                      }`}>
                        {tab?.label}
                      </h3>
                      <p className={`text-sm mt-1 ${
                        activeTab === tab?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {tab?.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg border border-border">
              {activeTab === 'users' && <AdminUserManagement />}
              {activeTab === 'coming-soon' && (
                <ComingSoonSettings 
                  settings={comingSoonSettings}
                  onUpdateSettings={handleComingSoonUpdate}
                />
              )}
              {activeTab === 'featured-content' && <FeaturedContentSettings />}
              {activeTab === 'system' && <SystemHealthPanel />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;